/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import lod_ from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Divider, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import { useDispatch, useSelector } from "react-redux";

import ConfirmDialog from "components/Custom/Dialogs/ConfirmDialog";
import DialogCreateFolder from "./DialogCreateFolder";
import MenuFolder from "./MenuFolder";
import action from "../../../redux-react/actions/sourceActions";
import formAction from "../../../redux-react/actions/formAction";
import themeActions from "redux-react/actions/themeActions";
import {
	setFolders as setFoldersRedux,
	selectFolders,
	updateFolder as updateFolderRedux,
	updateFolderByIdsPath,
	selectFolderByPath
} from "../../../redux-react/reducers/foldersReducer";
import { selectAssets } from "../../../redux-react/reducers/assetReducer";
import { selectPermissions } from "../../../redux-react/reducers/permissionsReducer";
import Assets from "./Assets/Assets";
import colors from "../../../assets/theme/base/colors";
import DialogCreateAsset from "./DialogAsset/DialogCreateAsset";
import AnswerTypeDialog from "components/Custom/AnswerTypeDialog";
import ThemeCard from "./Assets/Theme/ThemeCard";
import CategoryCard from "./Assets/Theme/CategoryCard";
import AddExternalPage from "../AddExternalPage";
import AddExternalVideo from "../AddExternalVideo";
import AddDocument from "../AddDocument";
import FileExplorer from "./Assets/TreeFolders";
import ThemeDisplay from "./Assets/Theme/Theme";
import { display } from "redux-react/reducers/snackBarReducer";
import { useThemeActions } from "hooks/useThemeActions";
import { useTranslateActions } from "hooks/useTranslateActions";

// Memoized filter function to prevent recalculation
const filterFolders = (folders = [], query = "", assets = []) => {
	const lowerQuery = query.toLowerCase();

	return folders
		.map(folder => {
			const filteredChildren = filterFolders(folder?.children || [], query, assets);

			return {
				...folder,
				isOpen: query ? true : folder?.isOpen,
				isSelected: query ? false : folder?.isSelected,
				children: filteredChildren
			};
		})
		.filter(folder => {
			const hasMatchingAssets = assets
				.filter(asset => asset.name.toLowerCase().includes(lowerQuery))
				.some(asset => asset.folderId === folder.id);

			return (
				folder.name.toLowerCase().includes(lowerQuery) || // Folder name matches
				folder.children.length > 0 || // Folder has filtered children
				hasMatchingAssets // Folder has matching assets
			);
		});
};

const SourceManagement = ({ assistantID, activeFilters }) => {
	const { palette } = useTheme();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const childRef = useRef(null);
	const { translateV2Dataset } = useTranslateActions();
	const { startCategoryTraining, startCategoryUntraining } = useThemeActions();

	// Redux selectors - stable references
	const folders = useSelector(selectFolders);
	const assets = useSelector(selectAssets);
	const permissions = useSelector(selectPermissions);

	// Derived state from permissions - memoized
	const permissionFlags = useMemo(() => {
		const role = permissions.role || "";
		const perms = permissions?.permissions || [];

		return {
			isWritter: role === "writer",
			isReviewer: role === "reviewer",
			isPublisher: role === "publisher",
			canDeleteFolder: perms.includes("access:deleteFolder"),
			canEditFolder: perms.includes("access:editFolder"),
			canCreateFolder: perms.includes("access:createFolder"),
			canCreateAsset: perms.includes("access:createAsset"),
			canDeleteAsset: perms.includes("access:deleteAsset"),
			canEditAsset: perms.includes("access:editAsset"),
			canChangeStatus: perms.includes("access:changeStatus"),
			canChangeAccess: perms.includes("access:changeAccess")
		};
	}, [permissions.role, permissions.permissions, assistantID]);

	// Local state
	const [specificActiveAsset, setSpecificActiveAsset] = useState("ALL");
	const [openAssetDialog, setOpenAssetDialog] = useState(false);
	const [openAssetEditDialog, setOpenAssetEditDialog] = useState(false);
	const [openAddExternalPage, setOpenAddExternalPage] = useState({
		open: false,
		format: null
	});

	const [openAddDocument, setOpenAddDocument] = useState({
		open: false,
		format: null
	});

	const [openAddExternalVideo, setOpenAddExternalVideo] = useState({
		open: false,
		format: null
	});

	// Theme part
	const [openCategoryDialog, setOpenCategoryDialog] = useState({
		open: false,
		parent: null,
		channelType: null
	});
	const [openEditCategoryDialog, setOpenEditCategoryDialog] = useState(false);

	const [localState, setLocalState] = useState({
		contextMenu: null,
		openFolderDialog: false,
		openRenameDialog: false,
		openDeleteDialog: false,
		folderName: "",
		selectedFolder: null,
		currentFolder: null,
		searchQuery: "",
		nameError: "",
		folderDescription: ""
	});

	/**
	 * File explorer controller
	 */
	const [selectedItems, setSelectedItems] = React.useState([]);
	const [expandedItems, setExpandedItems] = React.useState([]);

	const handleExpandedItemsChange = (event, itemIds) => {
		setExpandedItems(itemIds);
	};

	const handleSelectedItemsChange = (event, ids) => {
		setSelectedItems(ids);
	};

	const resetFileExplorerState = () => {
		setExpandedItems([]);
		setSelectedItems([]);
	};

	// Memoized filtered folders
	const filteredFolders = useMemo(
		() => filterFolders(folders, localState.searchQuery, assets),
		[folders, localState.searchQuery, assets]
	);

	// Memoized folder IDs
	const folderIdsFiltered = useMemo(() => {
		const extractIds = folderArray => {
			return folderArray.reduce((ids, folder) => {
				return [...ids, folder.id, ...extractIds(folder.children)];
			}, []);
		};
		return extractIds(filteredFolders);
	}, [filteredFolders, assistantID]);

	// Recursive function to map children at any level
	const mapChildrenRecursively = (children = [], stateChildren = []) => {
		return children.map(child => {
			const stateChild = stateChildren?.find(sf => sf.code === child.code);

			return {
				type: "folder",
				...child,
				id: child._id,
				isOpen: stateChild?.isOpen ?? false,
				isSelected: stateChild?.isSelected ?? false,
				children: mapChildrenRecursively(child.children ?? [], stateChild?.children ?? []),
				system: child?.system || false
			};
		});
	};

	// API calls - memoized with useCallback
	const getFolders = async () => {
		const onSuccess = resultFolders => {
			if (!resultFolders) {
				return;
			}

			const mapped = resultFolders.map((item, index) => {
				let stateFolder = folders.find(folder => folder.key === item.key);

				return {
					name: item?.name,
					parentId: item?.parentId,
					description: item?.description,
					id: item._id,
					children: mapChildrenRecursively(item.children ?? [], stateFolder?.children ?? []),
					isOpen: stateFolder?.isOpen ?? false,
					isSelected: stateFolder?.isSelected ?? false,
					assistantID,
					key: item?.key,
					allowedTypes: item?.allowedTypes,
					isEditable: item?.isEditable,
					allowBulkImport: item?.allowBulkImport,
					isDefaultFolder: item?.isDefaultFolder,
					rank: item.rank ?? index,
					type: "folder",
					system: item?.system || false
				};
			});
			dispatch(setFoldersRedux(mapped));
		};
		dispatch(
			action.getFolders(onSuccess, () => {
				dispatch(
					display({
						type: "error",
						message: t("SOURCES.FOLDER.GET.error")
					})
				);
			})
		);
	};

	useEffect(() => {
		getFolders();
	}, [assistantID, assistantID]);

	const handleClickSpecificList = useCallback(
		value => {
			setSpecificActiveAsset(value);
			// const recentSelected = folders?.find(it => it.isSelected);
			// if (recentSelected) {
			resetFileExplorerState();
			// }
			setLocalState(prev => ({
				...prev,
				currentFolder: null,
				selectedFolder: null
			}));
		},
		[folders, dispatch, assistantID]
	);

	// Event handlers - stable references with useCallback
	const handleSearchChange = useCallback(
		event => {
			const searchQuery = event.target.value;
			setLocalState(prev => ({
				...prev,
				searchQuery,
				selectedFolder: null,
				currentFolder: null
			}));
		},
		[assistantID]
	);

	const handleRightClick = useCallback(
		(event, folder) => {
			event.preventDefault();
			setLocalState(prev => ({
				...prev,
				contextMenu: {
					folder
				}
			}));
		},
		[assistantID]
	);

	const handleOpenMenu = useCallback(
		(event, folder) => {
			event.stopPropagation();
			setLocalState(prev => ({
				...prev,
				menuRef: event.currentTarget,
				contextMenu: {
					folder
				}
			}));
		},
		[assistantID]
	);

	const handleClose = useCallback(() => {
		setLocalState(prev => ({
			...prev,
			contextMenu: null,
			menuRef: null
		}));
	}, [assistantID]);

	const handleAddFolder = useCallback(() => {
		setLocalState(prev => ({
			...prev,
			openFolderDialog: true,
			folderName: "",
			nameError: "",
			selectedFolder: prev.contextMenu?.folder || prev.currentFolder || null
		}));
		handleClose();
	}, [handleClose, folders, assistantID]);

	const handleAddExternalPage = useCallback(
		pageType => {
			setLocalState(prev => ({
				...prev,
				selectedFolder: prev.contextMenu?.folder
			}));

			if (
				pageType === "WebPage" ||
				pageType === "sitemap" ||
				pageType === "shopifyPage" ||
				pageType === "wordpressPage"
			) {
				// Create asset
				setOpenAddExternalPage({
					open: true,
					format: pageType
				});
			}

			handleClose();
		},
		[handleClose, folders, assistantID]
	);

	const handleAddExternalVideo = useCallback(() => {
		setLocalState(prev => ({
			...prev,
			selectedFolder: prev.contextMenu?.folder
		}));

		setOpenAddExternalVideo({
			open: true,
			format: "videos"
		});
		handleClose();
	}, [handleClose, assistantID]);

	const handleAddFile = useCallback(
		(sourceFolder, pageType) => {
			setLocalState(prev => ({
				...prev,
				selectedFolder: prev.contextMenu?.folder
			}));

			if (sourceFolder?.fileType === "theme" || sourceFolder?.documentType === "answerType") {
				// When source folder is a theme type -> we're creating a new category
				setOpenCategoryDialog({
					open: true,
					parent: sourceFolder.code,
					channelType: sourceFolder?.type === "themeCategory" ? "themeQuestion" : "themeCategory"
				});
			} else if (["documents", "images"].includes(pageType)) {
				setOpenAddDocument({
					open: true,
					format: pageType
				});
			} else {
				// Default, create asset
				setOpenAssetDialog(true);
			}

			handleClose();
		},
		[handleClose, folders, assistantID]
	);

	const handleEditAsset = useCallback(
		asset => {
			setLocalState(prev => ({
				...prev,
				selectedAsset: asset
			}));

			if (asset?.documentType === "answerType") {
				// When source folder is a theme type -> we're creating a new category
				setOpenEditCategoryDialog(true);
			} else {
				// Default, create asset
				setOpenAssetEditDialog(true);
			}
			handleClose();
		},
		[handleClose, folders, assistantID]
	);

	const handleRenameFolder = useCallback(() => {
		setLocalState(prev => {
			if (!prev.contextMenu?.folder) return prev;

			return {
				...prev,
				openRenameDialog: true,
				folderName: prev.contextMenu.folder.name,
				folderDescription: prev.contextMenu.folder.description || "",
				nameError: "",
				selectedFolder: prev.contextMenu.folder
			};
		});
		handleClose();
	}, [handleClose, folders, assistantID]);

	const handleDeleteFolder = useCallback(
		folder => {
			switch (true) {
				case folder.fileType === "theme": {
					if (folder.code) {
						dispatch(
							themeActions.deleteTheme(
								folder.code,
								res => {
									getFolders();
								},
								error => {
									getFolders();
								}
							)
						);
					}
					break;
				}
				case folder.documentType === "answerType" && folder.type === "themeCategory": {
					dispatch(
						formAction.deleteItem(
							folder._id,
							"answerItem",
							{
								delete: true,
								actions: [
									"deleteChildAnswerType", // Answer type cave have childs with parent = answerItem.code, delete theme.
									"emptyCacheTheme" // Empty cache for theme
								]
							},
							() => getFolders()
						)
					);
					break;
				}
				case folder.documentType === "answerType" && folder.type === "themeQuestion": {
					dispatch(
						formAction.deleteItem(
							folder._id,
							"answerItem",
							{
								delete: true,
								actions: [
									"deleteThemeQuestionsKnowledge", // Delete all knowledge linked to this question
									"deleteChildAnswerType", // Answer type cave have childs with parent = answerItem.code, delete theme.
									"emptyCacheTheme" // Empty cache for theme
								]
							},
							() => getFolders()
						)
					);
					break;
				}
				default: {
					setLocalState(prev => {
						if (!prev.contextMenu?.folder) return prev;
						return {
							...prev,
							openDeleteDialog: true,
							selectedFolder: prev.contextMenu.folder
						};
					});
					break;
				}
			}

			handleClose();
		},
		[handleClose, folders, assistantID]
	);

	const handleDeleteConfirm = useCallback(() => {
		if (!localState.selectedFolder) return;

		const onSuccess = () => {
			handleClickSpecificList("ALL");
			resetFileExplorerState();
			getFolders();
		};

		dispatch(action.deleteFolder({ id: localState.selectedFolder.id }, assistantID, onSuccess));
		setLocalState(prev => ({
			...prev,
			openDeleteDialog: false,
			selectedFolder: null
		}));
	}, [dispatch, localState.selectedFolder, localState.currentFolder, assistantID, folders]);

	const handleCloseFolderDialog = useCallback(() => {
		setLocalState(prev => ({
			...prev,
			openFolderDialog: false,
			openRenameDialog: false,
			openDeleteDialog: false,
			folderName: "",
			nameError: "",
			selectedFolder: null,
			folderDescription: ""
		}));
	}, [assistantID]);

	const handleCloseAssetDialog = useCallback(() => {
		setOpenAssetDialog(false);
	}, [assistantID]);

	const isDuplicateName = useCallback(
		(nodes, name, parentId, currentFolderId = null) => {
			const checkNode = node =>
				node.name.toLowerCase() === name.toLowerCase() &&
				node.parentId === parentId &&
				node.id !== currentFolderId;

			const traverse = items =>
				items.some(item => {
					if (checkNode(item)) return true;
					return item.children.length ? traverse(item.children) : false;
				});

			return traverse(nodes);
		},
		[assistantID]
	);

	const handleFolderNameChange = useCallback(
		event => {
			const { value } = event.target;
			setLocalState(prev => ({
				...prev,
				folderName: value,
				nameError: value ? "" : prev.nameError
			}));
		},
		[assistantID]
	);

	const handleFolderDescriptionChange = useCallback(
		event => {
			const { value } = event.target;
			setLocalState(prev => ({
				...prev,
				folderDescription: value
			}));
		},
		[assistantID]
	);

	const handleAddFolderConfirm = useCallback(async () => {
		const folderName = localState.folderName.trim();
		if (!folderName) {
			setLocalState(prev => ({
				...prev,
				nameError: t("SOURCES.FOLDER.folderNameEmptyError")
			}));
			return;
		}

		const parentId = localState.selectedFolder?.id || null;
		if (isDuplicateName(folders, folderName, parentId)) {
			setLocalState(prev => ({
				...prev,
				nameError: t("SOURCES.FOLDER.folderNameDuplicateError", { name: folderName })
			}));
			return;
		}

		const newFolder = {
			name: folderName,
			parentId,
			assistantID,
			description: localState.folderDescription.trim(),
			key: localState.selectedFolder?.key,
			allowedTypes: localState.selectedFolder?.allowedTypes,
			isEditable: localState.selectedFolder?.isEditable,
			allowBulkImport: localState.selectedFolder?.allowBulkImport,
			rank: parentId ? 0 : filteredFolders.length // Set rank for new folder
		};

		if (parentId) {
			const folderParent = folders?.find(it => it.id === parentId);
			if (folderParent) {
				dispatch(updateFolderRedux({ ...folderParent, isOpen: true }));
			}
		}

		try {
			const onSuccess = res => {
				// dispatch(
				// 	createFolder({
				// 		...newFolder,
				// 		id: res?.folder?.insertedId,
				// 		children: [],
				// 		isOpen: false,
				// 		isSelected: false,
				// 		description: newFolder.description,
				// 		rank: newFolder.rank
				// 	})
				// );
				getFolders();
			};

			dispatch(action.createFolder({ ...newFolder, code: null }, assistantID, onSuccess));

			setLocalState(prev => ({
				...prev,
				openFolderDialog: false,
				folderName: "",
				folderDescription: "",
				nameError: ""
			}));
		} catch (error) {
			setLocalState(prev => ({
				...prev,
				nameError: t("SOURCES.FOLDER.CREATE.error")
			}));
		}
	}, [
		localState.folderName,
		localState.folderDescription,
		localState.selectedFolder,
		folders,
		isDuplicateName,
		t,
		assistantID,
		dispatch,
		folders,
		filteredFolders.length
	]);

	const handleRenameFolderConfirm = useCallback(() => {
		const folderName = localState.folderName.trim();
		if (!folderName) {
			setLocalState(prev => ({
				...prev,
				nameError: t("SOURCES.FOLDER.folderNameEmptyError")
			}));
			return;
		}

		if (!localState.selectedFolder) return;

		const parentId = localState.selectedFolder.parentId;
		if (isDuplicateName(folders, folderName, parentId, localState.selectedFolder.id)) {
			setLocalState(prev => ({
				...prev,
				nameError: t("SOURCES.FOLDER.folderNameDuplicateError", { name: folderName })
			}));
			return;
		}

		const updatedFolder = {
			...localState.selectedFolder,
			name: folderName,
			description: localState.folderDescription.trim()
		};

		const onSuccess = () => dispatch(updateFolderRedux(updatedFolder));

		dispatch(
			action.updateFolder(
				{
					id: updatedFolder.id,
					name: folderName,
					parentId,
					assistantID,
					description: updatedFolder.description,
					rank: updatedFolder.rank // Preserve existing rank
				},
				assistantID,
				onSuccess
			)
		);

		setLocalState(prev => ({
			...prev,
			openRenameDialog: false,
			folderName: "",
			folderDescription: "",
			nameError: "",
			currentFolder:
				prev.currentFolder?.id === updatedFolder.id ? updatedFolder : prev.currentFolder
		}));
	}, [
		localState.selectedFolder,
		localState.folderName,
		localState.folderDescription,
		folders,
		isDuplicateName,
		t,
		assistantID,
		dispatch
	]);

	// Add state to track the path for selection
	const [currentPath, setCurrentPath] = useState([]);

	// Move useSelector to component level
	const selectedFolderByPath = useSelector(state =>
		currentPath.length > 0 ? selectFolderByPath(state, currentPath) : null
	);

	// Handle the selectedFolderByPath changes
	useEffect(() => {
		if (selectedFolderByPath && currentPath.length > 0) {
			setLocalState(prev => ({
				...prev,
				currentFolder: selectedFolderByPath,
				selectedFolder: selectedFolderByPath
			}));
		}
	}, [selectedFolderByPath, currentPath, assistantID]);

	const handleFolderClick = useCallback(
		(folder, path) => {
			const mustBeAlwaysOpen = false;

			const updatedFolder = {
				...folder,
				isSelected: mustBeAlwaysOpen ? true : !folder?.isSelected,
				isOpen: mustBeAlwaysOpen ? true : !folder?.isOpen
			};

			// Keep the path without the last element (the clicked folder)
			const firstPathPart = lod_.cloneDeep(path);
			firstPathPart.pop();

			dispatch(
				updateFolderByIdsPath({
					path,
					update: updatedFolder
				})
			);

			if (!lod_.isEmpty(firstPathPart) && folder.isSelected) {
				// Set the path to trigger the selector
				setCurrentPath(firstPathPart);
			} else {
				// Clear the path and update state directly
				setSpecificActiveAsset("");
				setCurrentPath([]);
				setLocalState(prev => ({
					...prev,
					currentFolder: !folder.isSelected ? folder : null,
					selectedFolder: !folder.isSelected ? folder : null
				}));
			}
		},
		[dispatch, folders]
	);

	const clearSearch = useCallback(() => {
		setLocalState(prev => ({ ...prev, searchQuery: "" }));
	}, [assistantID]);

	// Memoized asset component
	const assetComponent = useMemo(() => {
		const currentFolder = localState.currentFolder || localState.selectedFolder;

		if (currentFolder?.key === "theme") {
			return (
				<ThemeDisplay
					folders={folders}
					theme={currentFolder}
					handleDeleteFolder={handleDeleteFolder}
					onEdit={values => {
						handleEditAsset(values);
					}}
				/>
			);
		}

		if (currentFolder?.fileType === "theme") {
			return (
				<ThemeCard
					folders={folders}
					theme={currentFolder}
					handleDeleteFolder={handleDeleteFolder}
					onEdit={values => {
						handleEditAsset(values);
					}}
					getFolders={getFolders}
					onAddFile={handleAddFile}
					themeActions={{
						translateV2Dataset,
						startCategoryTraining,
						startCategoryUntraining
					}}
				/>
			);
		}

		if (currentFolder?.documentType === "answerType" && currentFolder?.type === "themeCategory") {
			const themeFolder = folders.find(f => f.key === "theme");
			const themes = themeFolder?.children || [];
			const parentTheme = themes.find(f => f.code === currentFolder.parent);

			return (
				<CategoryCard
					theme={parentTheme}
					folders={folders}
					answerItem={currentFolder}
					handleDeleteFolder={handleDeleteFolder}
					onEdit={values => {
						handleEditAsset(values);
					}}
					onAddFile={handleAddFile}
					themeActions={{
						translateV2Dataset,
						startCategoryTraining,
						startCategoryUntraining
					}}
				/>
			);
		}

		return (
			<Assets
				reloadAssets={getFolders}
				assistantID={assistantID}
				folder={localState.currentFolder}
				folderId={localState?.currentFolder?.id || localState?.currentFolder?._id}
				formattedFolders={folders}
				folderIdsFiltered={folderIdsFiltered}
				activeFilters={activeFilters}
				{...permissionFlags}
				ref={childRef}
				searchText={localState.searchQuery}
				acceptedFile={localState.selectedFolder?.key}
			/>
		);
	}, [
		assistantID,
		localState?.currentFolder,
		localState?.currentFolder?.id,
		folders,
		folderIdsFiltered,
		activeFilters,
		permissionFlags,
		localState.searchQuery,
		localState.selectedFolder?.key
	]);

	// Check if folder has assets for delete dialog
	const withAsset = useMemo(() => {
		return (
			assets?.find(it => it?.folderId === localState.selectedFolder?.id) ||
			folders?.find(it => it?.parentId === localState.selectedFolder?.id)
		);
	}, [assets, folders, localState.selectedFolder?.id, assistantID]);

	useEffect(() => {
		// On assistant switch, reset states
		handleClickSpecificList("ALL");
		resetFileExplorerState();
	}, [assistantID]);

	return (
		<Box
			display="flex"
			width="100%"
			sx={{ height: "calc(100vh - 9rem)", overflow: "hidden", mb: 7 }}
		>
			<Paper
				sx={{
					padding: 2,
					minWidth: "262px",
					overflowY: "auto",
					borderRadius: "0.75rem",
					boxShadow: "0 20px 27px 0 rgba(0, 0, 0, 0.05)"
				}}
			>
				<Box
					sx={{
						position: "sticky",
						top: 0,
						backgroundColor: palette.background.paper,
						zIndex: 1
					}}
				>
					<TextField
						fullWidth
						variant="outlined"
						placeholder={t("SOURCES.FOLDER.searchPlaceholder")}
						value={localState.searchQuery}
						onChange={handleSearchChange}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
								endAdornment: localState.searchQuery && (
									<InputAdornment position="end" sx={{ cursor: "pointer" }} onClick={clearSearch}>
										<CloseIcon />
									</InputAdornment>
								)
							}
						}}
					/>
				</Box>

				<Box>
					<List>
						<ListItem
							sx={{ cursor: "pointer" }}
							onClick={() => {
								handleClickSpecificList("ALL");
								childRef.current?.sortDate({ sortedBy: "name", asc: 1 });
							}}
						>
							<IconButton>
								<DescriptionIcon fontSize="small" />
							</IconButton>
							<ListItemText
								sx={{
									color: specificActiveAsset === "ALL" ? colors.info.main : "inherit"
								}}
								primary={
									<Typography sx={{ fontWeight: "300", fontSize: "0.875rem" }}>
										{t("SOURCES.FOLDER.allFile")}
									</Typography>
								}
							/>
						</ListItem>

						<ListItem
							sx={{ cursor: "pointer" }}
							onClick={() => {
								handleClickSpecificList("RECENT");
								childRef.current?.sortDate({ sortedBy: "updatedAt", asc: -1 });
							}}
						>
							<IconButton>
								<HistoryIcon fontSize="small" />
							</IconButton>
							<ListItemText
								sx={{
									color: specificActiveAsset === "RECENT" ? colors.info.main : "inherit"
								}}
								primary={
									<Typography sx={{ fontWeight: "300", fontSize: "0.875rem" }}>
										{t("SOURCES.FOLDER.recentUpdate")}
									</Typography>
								}
							/>
						</ListItem>

						<Divider
							sx={{
								my: 1,
								borderWidth: "1px"
							}}
						/>

						<FileExplorer
							folders={filteredFolders}
							depth={0}
							onClick={handleFolderClick}
							onContextMenu={handleRightClick}
							onOpenMenu={handleOpenMenu}
							localState={localState}
							setLocalState={setLocalState}
							expandedItems={expandedItems}
							handleExpandedItemsChange={handleExpandedItemsChange}
							selectedItems={selectedItems}
							handleSelectedItemsChange={handleSelectedItemsChange}
							{...permissionFlags}
						/>
					</List>
				</Box>
			</Paper>

			<Box sx={{ width: "100%", overflow: "hidden", marginLeft: 2 }}>{assetComponent}</Box>

			{Boolean(localState.contextMenu?.folder) && (
				<MenuFolder
					onAddFolder={handleAddFolder}
					onAddExternalPage={handleAddExternalPage}
					onAddExternalVideo={handleAddExternalVideo}
					onEditFolder={handleRenameFolder}
					handleEditAsset={handleEditAsset}
					onRenameFolder={handleRenameFolder}
					onDeleteFolder={handleDeleteFolder}
					onAddFile={handleAddFile}
					contextMenu={localState.contextMenu}
					onClose={handleClose}
					anchorEl={localState.menuRef}
					themeActions={{
						translateV2Dataset,
						startCategoryTraining,
						startCategoryUntraining
					}}
					{...permissionFlags}
				/>
			)}

			{localState.openFolderDialog && (
				<DialogCreateFolder
					title={t("SOURCES.FOLDER.addSubfolderTitle")}
					onConfirm={handleAddFolderConfirm}
					onCancel={handleCloseFolderDialog}
					nameError={localState.nameError}
					folderName={localState.folderName}
					onChangeNameChange={handleFolderNameChange}
					description={localState.folderDescription}
					onChangeDescriptionChange={handleFolderDescriptionChange}
				/>
			)}

			{localState.openRenameDialog && (
				<DialogCreateFolder
					title={t("SOURCES.FOLDER.renameFolderTitle")}
					onConfirm={handleRenameFolderConfirm}
					onCancel={handleCloseFolderDialog}
					nameError={localState.nameError}
					folderName={localState.folderName}
					onChangeNameChange={handleFolderNameChange}
					isEditMode
					description={localState.folderDescription}
					onChangeDescriptionChange={handleFolderDescriptionChange}
				/>
			)}

			{openAssetDialog && (
				<DialogCreateAsset
					asset={localState.selectedFolder}
					folderId={localState.selectedFolder?.id || null}
					assistantID={assistantID}
					onClose={handleCloseAssetDialog}
					acceptedFile={localState.selectedFolder?.key}
					getFolders={getFolders}
				/>
			)}

			<AddExternalPage
				folderId={localState.selectedFolder?.id || null}
				getFolders={getFolders}
				open={openAddExternalPage.open}
				format={openAddExternalPage.format}
				onClose={() =>
					setOpenAddExternalPage({
						open: false,
						format: null
					})
				}
			/>

			<AddDocument
				folderId={localState.selectedFolder?.id || null}
				getFolders={getFolders}
				open={openAddDocument.open}
				format={openAddDocument.format}
				onClose={() =>
					setOpenAddDocument({
						open: false,
						format: null
					})
				}
			/>

			<AddExternalVideo
				folderId={localState.selectedFolder?.id || null}
				getFolders={getFolders}
				open={openAddExternalVideo.open}
				format={openAddExternalVideo.format}
				onClose={() =>
					setOpenAddExternalVideo({
						open: false,
						format: null
					})
				}
			/>

			{openAssetEditDialog && (
				<DialogCreateAsset
					folderId={localState.selectedFolder?.id}
					assistantID={assistantID}
					asset={localState.selectedAsset}
					isEdit
					onClose={() => setOpenAssetEditDialog(false)}
					getFolders={getFolders}
				/>
			)}

			<AnswerTypeDialog
				open={openCategoryDialog?.open}
				answerItem={null}
				handleClose={() =>
					setOpenCategoryDialog({
						open: false,
						parent: null,
						channelType: null
					})
				}
				handleSave={(item, edit) => {
					setOpenCategoryDialog({
						open: false,
						parent: null,
						channelType: null
					});
					getFolders();
				}}
				dictionary={{}}
				channels={[]}
				allowedChannels={[]}
				channelType={openCategoryDialog.channelType}
				//
				extra={{
					parent: openCategoryDialog?.parent || null
				}}
				//
				displayAlternativePannel={false}
				displayChannelsInput={false}
				displayCategoriesInput={false}
				displayCockpitInput={false}
				displayTipBar={false}
				displayNameInput={false}
				displayDescriptionInput={false}
				displayActiveInput={false}
			/>

			<AnswerTypeDialog
				open={openEditCategoryDialog}
				answerItem={localState.selectedAsset?._id}
				handleClose={() => setOpenEditCategoryDialog(false)}
				handleSave={(item, edit) => {
					setOpenEditCategoryDialog(false);
					getFolders();
				}}
				dictionary={{}}
				channels={[]}
				allowedChannels={[]}
				channelType={localState.selectedAsset?.type}
				//
				displayAlternativePannel={false}
				displayChannelsInput={false}
				displayCategoriesInput={false}
				displayCockpitInput={false}
				displayTipBar={false}
				displayNameInput={false}
				displayDescriptionInput={false}
				displayActiveInput={false}
			/>

			{localState.openDeleteDialog && (
				<ConfirmDialog
					title={t("SOURCES.FOLDER.confirmDeleteTitle")}
					content={
						withAsset
							? t("SOURCES.FOLDER.confirmDeleteContentWithAsset", {
									name: localState.selectedFolder?.name
								})
							: t("SOURCES.FOLDER.confirmDeleteContent", {
									name: localState.selectedFolder?.name
								})
					}
					open
					handleClose={handleCloseFolderDialog}
					handleConfirm={handleDeleteConfirm}
					isDelete
				/>
			)}
		</Box>
	);
};

SourceManagement.propTypes = {
	assistantID: PropTypes.string.isRequired,
	activeFilters: PropTypes.any
};

export default React.memo(SourceManagement);
